import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { Button } from './Button.jsx';

const Modal = ({
  open,
  onOpenChange,
  isOpen,
  onClose,
  children,
  size = 'md',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className,
  ...props
}) => {
  const sizes = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    full: 'max-w-full'
  };

  const resolvedOpen = typeof open === 'boolean' ? open : Boolean(isOpen);

  const handleOpenChange = (nextOpen) => {
    if (typeof onOpenChange === 'function') {
      onOpenChange(nextOpen);
    }
    if (!nextOpen && typeof onClose === 'function') {
      onClose();
    }
  };

  const modalClasses = clsx(
    'fixed left-1/2 top-1/2 z-[4000] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-xl focus:outline-none',
    sizes[size],
    className
  );

  return (
    <Dialog.Root open={resolvedOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[3990] bg-black/50 backdrop-blur-[1px]" />
        <Dialog.Content
          className={modalClasses}
          data-otg-component={props['data-otg-component'] || 'Modal'}
          onEscapeKeyDown={(event) => {
            if (!closeOnEscape) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (!closeOnBackdropClick) {
              event.preventDefault();
            }
          }}
          {...props}
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

const ModalHeader = ({ children, className, onClose, showCloseButton = true, closeLabel = 'Close modal', ...props }) => {
  const classes = clsx(
    'px-6 py-4 border-b border-gray-200 flex items-center justify-between',
    className
  );

  return (
    <div className={classes} {...props} data-otg-component={props['data-otg-component'] || 'ModalHeader'}>
      <div className="flex-1">{children}</div>
      {showCloseButton ? (
        onClose ? (
          <button
            onClick={onClose}
            className="ml-4 text-gray-400 transition-colors hover:text-gray-600"
            aria-label={closeLabel}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        ) : (
          <Dialog.Close asChild>
            <button
              className="ml-4 text-gray-400 transition-colors hover:text-gray-600"
              aria-label={closeLabel}
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </Dialog.Close>
        )
      ) : null}
    </div>
  );
};

const ModalBody = ({ children, className, ...props }) => {
  const classes = clsx('px-6 py-4', className);

  return (
    <div className={classes} {...props} data-otg-component={props['data-otg-component'] || 'ModalBody'}>
      {children}
    </div>
  );
};

const ModalFooter = ({ children, className, ...props }) => {
  const classes = clsx(
    'px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3 rounded-b-lg',
    className
  );

  return (
    <div className={classes} {...props} data-otg-component={props['data-otg-component'] || 'ModalFooter'}>
      {children}
    </div>
  );
};

const ModalTitle = ({ children, className, ...props }) => {
  const classes = clsx('text-lg font-semibold text-gray-900', className);

  return (
    <Dialog.Title className={classes} {...props} data-otg-component={props['data-otg-component'] || 'ModalTitle'}>
      {children}
    </Dialog.Title>
  );
};

const ModalDescription = ({ children, className, ...props }) => {
  const classes = clsx('text-sm text-gray-600 mt-1', className);

  return (
    <Dialog.Description className={classes} {...props} data-otg-component={props['data-otg-component'] || 'ModalDescription'}>
      {children}
    </Dialog.Description>
  );
};

const ConfirmDialog = ({
  open,
  onOpenChange,
  isOpen,
  onClose,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  confirmVariant = 'primary',
  confirmLoading = false,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  disableClose = false,
  size = 'sm',
}) => {
  const handleClose = () => {
    if (!disableClose && typeof onClose === 'function') {
      onClose();
    }
  };

  const allowDismiss = !disableClose && !confirmLoading;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      isOpen={isOpen}
      onClose={handleClose}
      size={size}
      closeOnBackdropClick={allowDismiss && closeOnBackdropClick}
      closeOnEscape={allowDismiss && closeOnEscape}
    >
      <Modal.Header onClose={allowDismiss ? handleClose : undefined} showCloseButton={allowDismiss}>
        <Modal.Title>{title}</Modal.Title>
        {description ? <Modal.Description>{description}</Modal.Description> : null}
      </Modal.Header>
      {children ? <Modal.Body>{children}</Modal.Body> : null}
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={!allowDismiss}>
          {cancelLabel}
        </Button>
        <Button variant={confirmVariant} onClick={onConfirm} loading={confirmLoading}>
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const FormDialog = ({
  open,
  onOpenChange,
  isOpen,
  onClose,
  title,
  description,
  children,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  submitVariant = 'primary',
  submitting = false,
  size = 'md',
  closeOnBackdropClick = true,
  closeOnEscape = true,
  disableClose = false,
  bodyClassName,
}) => {
  const allowDismiss = !disableClose && !submitting;

  const handleClose = () => {
    if (allowDismiss && typeof onClose === 'function') {
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      isOpen={isOpen}
      onClose={handleClose}
      size={size}
      closeOnBackdropClick={allowDismiss && closeOnBackdropClick}
      closeOnEscape={allowDismiss && closeOnEscape}
    >
      <form onSubmit={onSubmit}>
        <Modal.Header onClose={allowDismiss ? handleClose : undefined} showCloseButton={allowDismiss}>
          <Modal.Title>{title}</Modal.Title>
          {description ? <Modal.Description>{description}</Modal.Description> : null}
        </Modal.Header>
        <Modal.Body className={bodyClassName}>{children}</Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={!allowDismiss}>
            {cancelLabel}
          </Button>
          <Button type="submit" variant={submitVariant} loading={submitting}>
            {submitLabel}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

const SelectionDialog = ({
  open,
  onOpenChange,
  isOpen,
  onClose,
  title,
  description,
  options,
  selectedValue,
  onSelect,
  onConfirm,
  confirmLabel = 'Apply',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  confirmLoading = false,
  size = 'md',
  emptyLabel = 'No options available.',
}) => {
  return (
    <Modal open={open} onOpenChange={onOpenChange} isOpen={isOpen} onClose={onClose} size={size}>
      <Modal.Header onClose={onClose}>
        <Modal.Title>{title}</Modal.Title>
        {description ? <Modal.Description>{description}</Modal.Description> : null}
      </Modal.Header>
      <Modal.Body>
        {Array.isArray(options) && options.length > 0 ? (
          <div className="space-y-2">
            {options.map((option) => {
              const checked = option.value === selectedValue;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSelect(option.value)}
                  className={clsx(
                    'flex w-full items-start justify-between rounded-lg border px-3 py-3 text-left transition-colors',
                    checked
                      ? 'border-blue-500 bg-blue-50 text-blue-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  )}
                >
                  <span>
                    <span className="block text-sm font-medium">{option.label}</span>
                    {option.description ? (
                      <span className="mt-1 block text-xs text-gray-500">{option.description}</span>
                    ) : null}
                  </span>
                  <span
                    className={clsx(
                      'ml-4 mt-1 inline-flex h-4 w-4 rounded-full border',
                      checked ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                    )}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{emptyLabel}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>{cancelLabel}</Button>
        <Button variant={confirmVariant} onClick={onConfirm} loading={confirmLoading}>
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.Title = ModalTitle;
Modal.Description = ModalDescription;
Modal.ConfirmDialog = ConfirmDialog;
Modal.FormDialog = FormDialog;
Modal.SelectionDialog = SelectionDialog;

export default Modal;
export { ConfirmDialog, FormDialog, SelectionDialog };
